import { FRAME_COLORS, MATERIAL_TYPES } from '@mixtiles/web-backend-shared'
import { DYNAMIC_WALL_BG_MOBILE } from 'pages/AICreation/AICreationFlow/consts'
import type { ThumbCrop, TilesThumbData } from 'stores/cartStore/CartItem'

export type UpsellWall = {
  tileStyle: Omit<TilesThumbData, 'thumbUrl' | 'crop' | 'filterImageUrl'>
  photos: { thumbUrl: string; crop: ThumbCrop }[]
}

const GALLERY_WALL_TEXTURES_URL =
  'https://res.cloudinary.com/mixtiles/image/upload/f_auto,q_auto:good/assets/frames/galleryWallV2'

// The gallery wall editor's flat frame and shadow textures. Its frameRenderer pulls in pixi, so the bag keeps its own URLs.
const GALLERY_WALL_CLASSIC_FRAME_URLS: Partial<Record<FRAME_COLORS, string>> = {
  [FRAME_COLORS.BLACK]: `${GALLERY_WALL_TEXTURES_URL}/Black.png`,
  [FRAME_COLORS.WHITE]: `${GALLERY_WALL_TEXTURES_URL}/White.png`,
  [FRAME_COLORS.WOOD]: `${GALLERY_WALL_TEXTURES_URL}/Wood.png`,
}

export const GALLERY_WALL_SHADOW_URL =
  'https://res.cloudinary.com/mixtiles/image/upload/v1746452255/assets/frames/galleryWallV2/Shadow5-transparent-background.png'

export function getUpsellWallFrameUrl({
  materialType,
  frameColor,
}: UpsellWall['tileStyle']): string {
  const frameUrl =
    materialType === MATERIAL_TYPES.CLASSIC &&
    GALLERY_WALL_CLASSIC_FRAME_URLS[frameColor]
  if (!frameUrl) {
    throw new Error(
      `The upsell wall has no frame for ${frameColor} ${materialType} tiles`
    )
  }
  return frameUrl
}

// Everything UpsellWallSheet loads for a wall: its background, the tiles' frame and shadow, and each tile's photo.
export function getUpsellWallImageUrls(wall: UpsellWall): string[] {
  return [
    DYNAMIC_WALL_BG_MOBILE,
    getUpsellWallFrameUrl(wall.tileStyle),
    GALLERY_WALL_SHADOW_URL,
    ...wall.photos.map(({ thumbUrl }) => thumbUrl),
  ]
}

type UpsellWallTileStyle = UpsellWall['tileStyle']

const MAX_SINGLE_ROW_TILES = 3

// DynamicWallPreview's layouts: one row up to 3 tiles, then a near-square grid.
export function getUpsellWallColumns(tilesCount: number): number {
  return tilesCount <= MAX_SINGLE_ROW_TILES
    ? tilesCount
    : Math.ceil(Math.sqrt(tilesCount))
}

export type UpsellTile = Pick<
  UpsellWallTileStyle,
  'tileSize' | 'materialType'
> & {
  customStyle: Omit<UpsellWallTileStyle, 'tileSize' | 'materialType'>
}

export function getTileMiniStyle({
  tileSize,
  materialType,
  customStyle,
}: UpsellTile): UpsellWallTileStyle {
  return { tileSize, materialType, ...customStyle }
}
